import { usePWAUpdate, hardRefresh } from '@/hooks/usePWAUpdate';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';

/**
 * PWA update banner  -  shown when a new version is available. The member chooses when.
 *
 * VA-249: this used to run its own 1.5s reload timer while the hook ran a 2s one, so two
 * timers raced and the shorter one usually won — cutting off a service worker handover
 * that was about to finish, then reloading into the same cached build anyway. The fallback
 * lives in the hook now, where it can actually clear the caches serving the old files.
 */
export function PWAUpdateBanner() {
  const { hasUpdate, applyUpdate } = usePWAUpdate();
  const [isUpdating, setIsUpdating] = useState(false);

  if (!hasUpdate) return null;

  const handleClick = () => {
    if (isUpdating) return; // Prevent double-tap
    setIsUpdating(true);

    // One owner for the update, and one owner for the fallback. applyUpdate escalates to
    // clearing caches and unregistering the worker by itself if the handover stalls, so
    // there is nothing to race it with from here.
    try {
      applyUpdate();
    } catch (e) {
      console.error('PWA: applyUpdate failed, forcing a clean reload:', e);
      void hardRefresh();
    }
  };

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+1rem)] left-1/2 z-[70] -translate-x-1/2 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <button
        onClick={handleClick}
        disabled={isUpdating}
        className="flex items-center gap-2 rounded-full border border-border/60 bg-card/95 px-3 py-2 text-xs font-semibold text-foreground shadow-lg backdrop-blur hover:bg-muted transition-colors disabled:opacity-70"
        aria-label={isUpdating ? "Updating app" : "Update app"}
      >
        <RefreshCw className={`h-4 w-4 ${isUpdating ? 'animate-spin' : ''}`} />
        {isUpdating ? 'Getting the latest version…' : 'Update app'}
      </button>
    </div>
  );
}
